import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { MapPin } from "lucide-react";
import { FormGroup } from "../components/ui/form-group";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const apiUrl = process.env.REACT_APP_API_URL;

const SignUpPage = () => {
  const navigate = useNavigate();

  // Creates both a User and a Veterinary record via the existing
  // /api/v1/veterinaries/veterinary_signup endpoint.
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    address: "",
    dni: "",
    specialization: "",
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [registeredUser, setRegisteredUser] = useState(null);
  const [resendState, setResendState] = useState({ loading: false, message: "" });

  const getAddressSuggestions = async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(
        `${apiUrl}/api/v1/mapbox/address_suggestions`,
        { params: { query } },
      );
      setAddressSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setAddressSuggestions([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "address") {
      getAddressSuggestions(value);
    }
  };

  const handleSuggestionSelect = (selectedAddress) => {
    setFormData((prev) => ({ ...prev, address: selectedAddress }));
    setAddressSuggestions([]);
  };

  const signup = (data) => {
    const {
      email,
      password,
      firstName,
      lastName,
      address,
      phoneNumber,
      dni,
      specialization,
    } = data;

    const formDataToSend = new FormData();
    // User fields
    formDataToSend.append("user[email]", email);
    formDataToSend.append("user[password]", password);
    formDataToSend.append("user[first_name]", firstName);
    formDataToSend.append("user[last_name]", lastName);
    formDataToSend.append("user[address]", address);
    formDataToSend.append("user[phone_number]", phoneNumber);
    // Veterinary fields
    formDataToSend.append("veterinary[dni]", dni);
    formDataToSend.append("veterinary[specialization]", specialization);

    return axios.post(`${apiUrl}/api/v1/veterinaries/veterinary_signup`, formDataToSend, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  const validate = () => {
    const formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    if (!formData.firstName.trim())
      formErrors.firstName = "First name is required";
    if (!formData.lastName.trim())
      formErrors.lastName = "Last name is required";
    if (!formData.email || !emailRegex.test(formData.email)) {
      formErrors.email = "Please enter a valid email address";
    }
    if (!formData.phoneNumber.trim())
      formErrors.phoneNumber = "Phone number is required";
    if (!formData.address.trim()) formErrors.address = "Address is required";
    if (!formData.dni.trim()) formErrors.dni = "DNI is required";
    if (!formData.specialization.trim())
      formErrors.specialization = "Specialization is required";
    if (formData.password.length < 6) {
      formErrors.password = "Password must be at least 6 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      formErrors.confirmPassword = "Passwords do not match";
    }
    return formErrors;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setSubmitError("");

    const formErrors = validate();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await signup(formData);
      setRegisteredUser(response.data.user);
    } catch (error) {
      console.error("Sign-up error:", error);
      const responseErrors = error.response?.data?.errors;
      setSubmitError(
        (Array.isArray(responseErrors) && responseErrors.join(" ")) ||
          error.response?.data?.error ||
          "Sign-up failed. Please check your details and try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!registeredUser?.id) return;
    setResendState({ loading: true, message: "" });
    try {
      const response = await axios.post(
        `${apiUrl}/api/v1/resend_confirmation`,
        { user: { id: registeredUser.id } },
      );
      setResendState({
        loading: false,
        message: response.data.message || "Confirmation email sent.",
      });
    } catch (error) {
      setResendState({
        loading: false,
        message:
          error.response?.data?.errors?.[0] ||
          "Failed to resend confirmation email. Please try again.",
      });
    }
  };

  if (registeredUser) {
    return (
      <div style={styles.container}>
        <div style={styles.logoContainer}>
          <img src="/carepetai-logo.svg" alt="Logo" style={styles.logo} />
        </div>

        <div style={styles.formContainer}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Check Your Email</h2>
            <p style={styles.successMessage}>
              We've sent a confirmation link to{" "}
              <strong>{registeredUser.email}</strong>. Please verify your
              email before logging in.
            </p>
            <p style={styles.note}>
              Please check your spam folder if you don't see the email.
            </p>

            <button
              type="button"
              onClick={handleResendConfirmation}
              style={styles.resendLink}
              disabled={resendState.loading}
            >
              {resendState.loading
                ? "Sending..."
                : "Resend confirmation email"}
            </button>
            {resendState.message && (
              <p style={styles.resendMessage}>{resendState.message}</p>
            )}

            <div style={styles.buttonContainer}>
              <Button
                type="button"
                variant="primary"
                fullWidth
                size="large"
                onClick={() => navigate("/login")}
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.logoContainer}>
        <img src="/carepetai-logo.svg" alt="Logo" style={styles.logo} />
      </div>

      <div style={styles.formContainer}>
        <div style={styles.headerContainer}>
          <h2 style={styles.title}>Create Your Professional Account</h2>
          <p style={styles.subtitle}>
            Register as a veterinary professional to manage your practice on
            CarePet.
          </p>
        </div>

        {submitError && <div style={styles.submitError}>{submitError}</div>}

        <form onSubmit={handleSignUp} style={styles.form}>
          <div style={styles.row}>
            <FormGroup
              label="First Name"
              required
              error={errors.firstName}
              style={{ flex: 1 }}
            >
              <Input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Jane"
                error={!!errors.firstName}
                disabled={loading}
              />
            </FormGroup>
            <FormGroup
              label="Last Name"
              required
              error={errors.lastName}
              style={{ flex: 1 }}
            >
              <Input
                type="text"
                name="lastName"
                id="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Doe"
                error={!!errors.lastName}
                disabled={loading}
              />
            </FormGroup>
          </div>

          <FormGroup label="Email" required error={errors.email}>
            <Input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              error={!!errors.email}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup
            label="Phone Number"
            required
            error={errors.phoneNumber}
          >
            <Input
              type="text"
              name="phoneNumber"
              id="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="Enter your phone number"
              error={!!errors.phoneNumber}
              disabled={loading}
            />
          </FormGroup>

          <div style={styles.addressWrapper}>
            <FormGroup label="Address" required error={errors.address}>
              <Input
                type="text"
                name="address"
                id="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Start typing your clinic address"
                error={!!errors.address}
                disabled={loading}
                autoComplete="off"
              />
            </FormGroup>

            {addressSuggestions.length > 0 && (
              <div style={styles.suggestionsContainer}>
                {addressSuggestions.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => handleSuggestionSelect(item)}
                    style={styles.suggestionItem}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f3f4f6")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <MapPin size={14} style={styles.locationIcon} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <FormGroup
            label="DNI"
            required
            error={errors.dni}
            hint={!errors.dni ? "Your national identification number" : undefined}
          >
            <Input
              type="text"
              name="dni"
              id="dni"
              value={formData.dni}
              onChange={handleChange}
              placeholder="Enter your DNI"
              error={!!errors.dni}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup
            label="Specialization"
            required
            error={errors.specialization}
          >
            <Input
              type="text"
              name="specialization"
              id="specialization"
              value={formData.specialization}
              onChange={handleChange}
              placeholder="e.g. Small animal surgery"
              error={!!errors.specialization}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup
            label="Password"
            required
            error={errors.password}
            hint={!errors.password ? "At least 6 characters" : undefined}
          >
            <Input
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Create a password"
              error={!!errors.password}
              disabled={loading}
            />
          </FormGroup>

          <FormGroup
            label="Confirm Password"
            required
            error={errors.confirmPassword}
            marginBottom="24px"
          >
            <Input
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              error={!!errors.confirmPassword}
              disabled={loading}
            />
          </FormGroup>

          <div style={styles.buttonContainer}>
            <Button
              type="submit"
              variant="primary"
              fullWidth
              size="large"
              disabled={loading}
            >
              {loading ? "Creating Account..." : "Create Account"}
            </Button>
          </div>

          <div style={styles.loginLinkContainer}>
            <span style={styles.loginLinkText}>
              Already have an account?{" "}
            </span>
            <Link to="/login" style={styles.loginLink}>
              Log in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "60px 0",
    minHeight: "100vh",
    position: "relative",
    fontFamily: "'Poppins', sans-serif",
    backgroundColor: "#f9fafb",
  },
  logoContainer: {
    textAlign: "center",
    margin: "10px 0 30px 0",
  },
  logo: {
    width: "300px",
    height: "50px",
    objectFit: "contain",
  },
  formContainer: {
    padding: "40px",
    maxWidth: "480px",
    margin: "0 auto",
    backgroundColor: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  },
  headerContainer: {
    marginBottom: "24px",
    textAlign: "center",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "8px",
    color: "#111827",
    lineHeight: "1.2",
  },
  subtitle: {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    margin: "0 auto",
    maxWidth: "400px",
  },
  form: {
    width: "100%",
  },
  row: {
    display: "flex",
    gap: "16px",
  },
  addressWrapper: {
    position: "relative",
  },
  suggestionsContainer: {
    position: "absolute",
    top: "calc(100% - 12px)",
    left: 0,
    right: 0,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
    boxShadow:
      "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    maxHeight: "220px",
    overflowY: "auto",
    zIndex: 10,
  },
  suggestionItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    fontSize: "14px",
    color: "#374151",
    cursor: "pointer",
    borderBottom: "1px solid #f3f4f6",
  },
  locationIcon: {
    flexShrink: 0,
    color: "#6b7280",
  },
  buttonContainer: {
    marginTop: "8px",
  },
  submitError: {
    backgroundColor: "#FEE2E2",
    color: "#B91C1C",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "14px",
    marginBottom: "20px",
  },
  loginLinkContainer: {
    marginTop: "20px",
    textAlign: "center",
    fontSize: "14px",
  },
  loginLinkText: {
    color: "#6b7280",
  },
  loginLink: {
    color: "#F97316",
    textDecoration: "underline",
    fontWeight: "500",
  },
  successContainer: {
    textAlign: "center",
    padding: "20px",
  },
  successIcon: {
    width: "64px",
    height: "64px",
    backgroundColor: "#10b981",
    color: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    fontWeight: "bold",
    margin: "0 auto 24px auto",
  },
  successTitle: {
    fontSize: "24px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#111827",
  },
  successMessage: {
    fontSize: "16px",
    color: "#374151",
    lineHeight: "1.5",
    marginBottom: "12px",
  },
  note: {
    fontSize: "14px",
    color: "#6b7280",
    fontStyle: "italic",
    lineHeight: "1.5",
    marginBottom: "16px",
  },
  resendLink: {
    background: "none",
    border: "none",
    color: "#F97316",
    fontSize: "14px",
    fontWeight: "600",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  resendMessage: {
    margin: "10px 0 0 0",
    fontSize: "13px",
    color: "#6b7280",
  },
};

export default SignUpPage;
